import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { handleApiError } from './axiosConfig';
import { DiscountCode } from '../utils/types';

interface DiscountCodesResponse {
  discount_codes: DiscountCode[];
}

interface CreateDiscountCodePayload {
  code: string;
  camp_id: number | null;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  is_single_use: boolean;
}

// GET /api/admin/discount-codes
export const useGetDiscountCodes = () => {
  return useQuery({
    queryKey: ['discount-codes'],
    queryFn: async (): Promise<DiscountCodesResponse> => {
      try {
        return await apiClient.get<DiscountCodesResponse>('/api/admin/discount-codes');
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to fetch discount codes:', errorMessage);
        throw new Error(errorMessage);
      }
    },
  });
};

// POST /api/admin/discount-codes
export const useCreateDiscountCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateDiscountCodePayload): Promise<DiscountCode> => {
      try {
        return await apiClient.post<DiscountCode>('/api/admin/discount-codes', data);
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to create discount code:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discount-codes'] });
    },
  });
};

// PATCH /api/admin/discount-codes/{id} — toggle is_active
export const useToggleDiscountCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }): Promise<DiscountCode> => {
      try {
        return await apiClient.patch<DiscountCode>(`/api/admin/discount-codes/${id}`, { is_active });
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Failed to toggle discount code:', errorMessage);
        throw new Error(errorMessage);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discount-codes'] });
    },
  });
};
